import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { NbToastrService } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import { Subject, forkJoin, of, takeUntil } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

import { DataSource } from '../../services/data-sources.service';
import { LmsPlacement } from '../../services/lms-registry.service';
import {
  DozentBot,
  Tutor,
  TutorService,
  TutorSettingItem,
} from '../../services/tutor.service';

interface SettingMeta {
  setting_id: string | null;
  variable_name: string;
  field_type: string;
}

@Component({
  // Attribute selector on a real <tr> — required so the host element
  // IS the table row, instead of wrapping a <tr> in an extra DOM
  // element which breaks the parent table's column alignment under
  // table-layout: fixed.
  selector: 'tr[app-tutor-kb-row]',
  templateUrl: './tutor-kb-row.component.html',
  styleUrl: './tutor-kb-row.component.scss',
  host: {
    '[class.tutor-row]': 'true',
    '[class.is-saving]': 'saving',
  },
})
export class TutorKbRowComponent implements OnInit, OnChanges, OnDestroy {
  @Input() tutor!: Tutor;
  @Input() dataSources: DataSource[] = [];
  @Input() dozentBots: DozentBot[] = [];
  /** All Moodle placements for THIS tutor, surfaced by the parent.
   *  Drives the per-row read-only "X von Y Kursen synct" badge. */
  @Input() placements: LmsPlacement[] = [];
  /** DataSource ids that mirror a registration's auto-sync bucket
   *  (provisioned implicitly by the per-placement course-sync toggle
   *  on the LMS tab). They stay visible in the KB picker so this row
   *  mirrors the agent's actual knowledge-base settings. */
  @Input() autoBucketDataSourceIds: Set<string> = new Set();

  // ─── Form state (local, applied on Save) ─────────────────────────
  kbIds: string[] = [];

  dozentEnabled = false;
  dozentBotId: string | null = null;

  /** Numeric model temperature. NULL/undefined when the tutor's
   * template doesn't expose it (e.g. alpha_flexible drops it). */
  temperature: number | null = null;

  // ─── Setting meta — required on save to know which setting_id to PATCH.
  //    Public so the template can `*ngIf` on its presence to decide
  //    whether to render the temperature input at all.
  temperatureMeta: SettingMeta | null = null;

  loading = false;
  saving = false;
  /** Keeps the "Save" button disabled until the user actually changes
   *  something. Re-set whenever load() repopulates from the server. */
  dirty = false;

  private destroy$ = new Subject<void>();

  constructor(
    private tutorService: TutorService,
    private toastr: NbToastrService,
    private t: TranslateService,
  ) {}

  ngOnInit(): void {
    this.load();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['tutor'] && !changes['tutor'].firstChange) {
      this.load();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ─── Loading ─────────────────────────────────────────────────────

  private load(): void {
    this.loading = true;
    this.dirty = false;

    // KB list comes straight from the tutor cache and mirrors the
    // agent settings exactly, including course-sync auto buckets.
    const allIds = this.tutor.tutor_data_source_ids || this.tutor.data_source_ids || [];
    this.kbIds = [...allIds];

    // Dozent link: present when attached_dozent_id is set on the bulk
    // tutor list response (no per-row fetch needed).
    this.dozentEnabled = !!this.tutor.attached_dozent_id;
    this.dozentBotId = this.tutor.attached_dozent_id || null;

    // template_settings — need this to find the temperature setting_id
    // for save. Course sync is placement-owned and intentionally not
    // editable in this agent-level quick config.
    this.tutorService
      .getTutorSettings(this.tutor.id)
      .pipe(
        takeUntil(this.destroy$),
        catchError(() => of([] as TutorSettingItem[])),
      )
      .subscribe({
        next: (items) => {
          this.applySettings(items);
          this.loading = false;
          this.dirty = false;
        },
        error: () => {
          this.loading = false;
        },
      });
  }

  private applySettings(items: TutorSettingItem[]): void {
    const temp = items.find((s) => s.variable_name === 'temperature');
    if (temp) {
      this.temperatureMeta = {
        setting_id: String(temp.setting_id),
        variable_name: 'temperature',
        field_type: temp.field_type,
      };
      const v = Number(temp.value);
      this.temperature = Number.isFinite(v) ? v : null;
    } else {
      this.temperatureMeta = null;
      this.temperature = null;
    }
  }

  // ─── Form change handlers ────────────────────────────────────────

  onKbIdsChange(ids: string[]): void {
    this.kbIds = ids;
    this.dirty = true;
  }

  onDozentToggle(checked: boolean): void {
    this.dozentEnabled = checked;
    if (!checked) this.dozentBotId = null;
    this.dirty = true;
  }

  onDozentBotChange(id: string | null): void {
    this.dozentBotId = id;
    this.dirty = true;
  }

  onTemperatureChange(value: number | null): void {
    this.temperature = value;
    this.dirty = true;
  }

  // ─── Save ────────────────────────────────────────────────────────

  save(): void {
    if (this.saving || !this.dirty) return;
    this.saving = true;

    // Build the parallel calls. Each can independently fail; we
    // surface the first error in a toast but still wait for all so
    // the user knows the row's final state.
    const calls: any[] = [];

    // 1) KB list — complete tutor-purpose list as shown in the picker.
    const currentKbIds = this.tutor.tutor_data_source_ids || this.tutor.data_source_ids || [];
    const targetKbIds = [...this.kbIds];
    if (!this.sameSet(targetKbIds, currentKbIds)) {
      calls.push(
        this.tutorService.updateTutor({
          id: this.tutor.id,
          tutor_data_source_ids: targetKbIds,
        } as Partial<Tutor>),
      );
    }

    // 2) Agent settings. Course sync is placement-owned, so this row
    //    only patches genuine agent settings such as temperature.
    const settingsPatch: Record<string, any> = {};
    if (
      this.temperatureMeta &&
      this.temperature != null &&
      Number.isFinite(this.temperature)
    ) {
      settingsPatch[this.temperatureMeta.setting_id as string] = this.temperature;
    }
    if (Object.keys(settingsPatch).length > 0) {
      calls.push(
        this.tutorService.updateTutorSettings(this.tutor.id, settingsPatch),
      );
    }

    // 3) Dozent link — attach / detach / change.
    const currentDozent = this.tutor.attached_dozent_id || null;
    const targetDozent = this.dozentEnabled ? this.dozentBotId : null;
    if (targetDozent !== currentDozent) {
      if (targetDozent) {
        calls.push(this.tutorService.attachDozent(this.tutor.id, targetDozent));
      } else {
        calls.push(this.tutorService.detachDozent(this.tutor.id));
      }
    }

    if (calls.length === 0) {
      this.saving = false;
      this.dirty = false;
      return;
    }

    forkJoin(calls)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.tutor.tutor_data_source_ids = [...this.kbIds];
          this.tutor.data_source_ids = [...this.kbIds];
          this.tutor.attached_dozent_id = this.dozentEnabled ? this.dozentBotId : null;
          this.saving = false;
          this.dirty = false;
          this.toastr.success(
            this.t.instant('pages.tutors.placements.kb.savedToast'),
            '',
          );
        },
        error: (err) => {
          this.saving = false;
          this.toastr.danger(
            err?.error?.detail || this.t.instant('toasts.error'),
            '',
          );
        },
      });
  }

  private sameSet(a: string[], b: string[]): boolean {
    if (a.length !== b.length) return false;
    const sa = new Set(a);
    return b.every((x) => sa.has(x));
  }

  // ─── Derived view-state for the Kurs-Sync badge ──────────────────

  /** True when the tutor is placed in at least one Moodle course.
   *  Gate for the read-only Kurs-Sync status badge. */
  get hasMoodlePlacements(): boolean {
    return this.placements.length > 0;
  }

  get totalPlacementCount(): number {
    return this.placements.length;
  }

  get syncedPlacementCount(): number {
    return this.placements.filter((p) => !!p.course_sync_enabled).length;
  }

  get kbDataSources(): DataSource[] {
    return this.dataSources;
  }

  isAutoBucketDataSource(id: string): boolean {
    return this.autoBucketDataSourceIds?.has(id) ?? false;
  }
}
